import React from "react";
import { useObserver } from "mobx-react";
import cns from "classnames";
import {
  Paper,
  ListItem,
  List,
  Divider,
  IconButton,
  Input,
  InputAdornment,
  Tooltip
} from "@material-ui/core";
import { Domain, Person, Search } from "@material-ui/icons";
import { Plus } from "mdi-material-ui";
import { makeStyles } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles/colorManipulator";
import { CustomerChildrenList_ItemFragment as Item } from "@graphql";
import { ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import { Body, PlainLink, Subheading, MarkHighlighter } from "@components";
import { store } from "@store";

export type CustomerChildrenListProps = {
  canAdd?: boolean;
  expanded?: boolean; // filter = no, children list = no, "click to expand" = yes
  items: Item[];
  onAddContactPerson?: () => void;
  onAddCustomer?: () => void;
  onClick: (customerId: string) => void;
  onHeaderClick: () => void;
  noScrollbar: boolean;
  title?: string;
};

export function CustomerChildrenList(props: CustomerChildrenListProps) {
  const { onClick, title } = props;
  const [search, setSearch] = React.useState("");
  const classes = useStyles({});

  const filteredItems = props.items
    .sortBy((x) => x.Name)
    .filter((x) => x.Name.toLowerCase().includes(search.toLowerCase()));

  return useObserver(() => (
    <Paper data-test="CustomerChildrenList" className={classes.root}>
      <div className={classes.titleBar}>
        {title && (
          <Subheading
            className={classes.title}
            clickable={!props.expanded}
            onClick={props.onHeaderClick}
          >
            {title}
          </Subheading>
        )}

        <Body style={{ marginLeft: "auto" }}>{props.items.length}</Body>
      </div>

      {!props.expanded && (
        <div onClick={props.onHeaderClick}>
          <Body style={{ padding: "0 16px", fontStyle: "italic" }}>
            {locale.ClickToOpen}
          </Body>
        </div>
      )}

      {props.items.length > 0 && (
        <div className={classes.search} style={{}}>
          <Input
            placeholder={locale.Search}
            onChange={(e) => setSearch(e.target.value)}
            endAdornment={
              <InputAdornment position={"end"}>
                <Search fontSize="small" />
              </InputAdornment>
            }
            fullWidth
          />
        </div>
      )}

      <Divider />

      <div className={cns({ [classes.scroll]: !props.noScrollbar })}>
        <MarkHighlighter highlightSearch={search}>
          {filteredItems.length === 0 && (
            <Body className={classes.padded}>{locale.Empty}</Body>
          )}

          <List style={{ paddingTop: 0, paddingBottom: 0 }}>
            {filteredItems.map((item) => (
              <ListItem
                key={item._id}
                className={cns(classes.item, classes.padded)}
                onClick={() => onClick(item._id)}
              >
                <PlainLink className="highlighter">{item.Name}</PlainLink>
              </ListItem>
            ))}
          </List>
        </MarkHighlighter>
      </div>

      <Divider />

      <div className={classes.actions}>
        <Tooltip title={locale["Add a New Workplace"]}>
          <IconButton
            classes={{ label: classes.actionBtnLabel }}
            className={classes.actionBtn}
            onClick={props.onAddCustomer}
            disabled={!store().user.hasPermission("CanAddCustomer")}
          >
            <Domain />
            <Plus className={classes.plus} />
          </IconButton>
        </Tooltip>

        <Tooltip title={locale["Add a New Contact"]}>
          <IconButton
            classes={{ label: classes.actionBtnLabel }}
            className={classes.actionBtn}
            onClick={props.onAddContactPerson}
          >
            <Person />
            <Plus className={classes.plus} />
          </IconButton>
        </Tooltip>
      </div>
    </Paper>
  ));
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    "&:not(:last-child)": {
      marginBottom: 16
    }
  },

  scroll: {
    maxHeight: 300,
    overflow: "auto"
  },

  titleBar: {
    alignItems: "center",
    display: "flex",
    padding: "8px 16px"
  },

  search: {
    padding: "8px 16px",
    "& svg": { color: theme.palette.grey[500] }
  },

  title: { padding: 0 },

  padded: { padding: "8px 16px" },

  item: {
    alignItems: "center",
    cursor: "pointer",
    display: "flex",
    listStyle: "none",
    flexBasis: "100%",

    [theme.breakpoints.down("sm")]: {
      flexBasis: "25%"
    },

    "&:hover": {
      backgroundColor: fade(theme.palette.text.primary, theme.palette.action.hoverOpacity)
    }
  },

  actions: {
    ...theme.mixins.childrenMarginRight(8),
    padding: 8,
    paddingLeft: 16,
    display: "flex"
  },

  actionBtn: {
    padding: 4,
    background: theme.palette.primary.main,
    color: "white",
    "& svg": { fontSize: 16 },

    "&:hover svg:last-child": {
      color: fade(theme.palette.action.active, theme.palette.action.hoverOpacity)
    }
  },

  actionBtnLabel: {
    position: "relative"
  },

  plus: {
    background: "white",
    borderRadius: 6,
    bottom: -5,
    color: theme.palette.primary.main,
    fontSize: 8,
    height: 12,
    position: "absolute",
    right: -7,
    width: 12,
    zIndex: 2
  }
}));
